import { Button } from 'primereact/button';
import { SyntheticEvent } from 'react';

import styles from '@features/campaigns/builder/components/FormFields/common/FileUpload/components/FileItemTemplate/FileItemTemplate.module.scss';
import { CREATIVE_CATEGORY, CreativeCategory } from '@features/campaigns/builder/types/creativeType.ts';

interface SwitchInThumbnail {
  leftImageUrl?: string;
  bottomImageUrl?: string;
}

interface FileItemTemplateProps {
  togglePreviewModal: () => void;
  fileName: string;
  fileURL: string;
  fileFormatSize: string;
  creativeCategory: CreativeCategory;
  deleteItemCallback?: (event: SyntheticEvent) => void;
  canDeleteFile: boolean;
  canShowPreview?: boolean;
  switchInThumbnail?: SwitchInThumbnail;
}

function SwitchInThumbnailPreview({ leftImageUrl, bottomImageUrl }: SwitchInThumbnail) {
  return (
    <div className={styles.switchInThumbnail}>
      {leftImageUrl && (
        <img
          alt='switch-in-left-thumbnail'
          role='presentation'
          src={leftImageUrl}
          className={styles.switchInLeftLayer}
          data-testid='switch-in-thumbnail-left'
        />
      )}
      {bottomImageUrl && (
        <img
          alt='switch-in-bottom-thumbnail'
          role='presentation'
          src={bottomImageUrl}
          className={styles.switchInBottomLayer}
          data-testid='switch-in-thumbnail-bottom'
        />
      )}
    </div>
  );
}

interface FileItemThumbnailProps {
  canShowPreview: boolean;
  creativeCategory: CreativeCategory;
  fileName: string;
  fileURL: string;
  hasSwitchInThumbnail: boolean;
  switchInThumbnail?: SwitchInThumbnail;
  togglePreviewModal: () => void;
}

function FileItemThumbnail({
  canShowPreview,
  creativeCategory,
  fileName,
  fileURL,
  hasSwitchInThumbnail,
  switchInThumbnail,
  togglePreviewModal,
}: FileItemThumbnailProps) {
  if (!canShowPreview) {
    return (
      <div className={styles.thumbnailPlaceholderWrapper}>
        <i className={'pi pi-image icon'} />
      </div>
    );
  }

  const previewOverlay = (
    <button
      className={styles.thumbnailOverlay}
      type='button'
      onClick={togglePreviewModal}
      data-testid={'open-preview-modal-button'}
    >
      <i className={'pi pi-search-plus icon'} />
    </button>
  );

  if (creativeCategory === CREATIVE_CATEGORY.VIDEO) {
    return (
      <>
        {previewOverlay}
        <div className={styles.thumbnailVideoWrapper}>
          <i className={'pi pi-video icon'} />
        </div>
      </>
    );
  }

  if (hasSwitchInThumbnail) {
    return (
      <>
        {previewOverlay}
        <SwitchInThumbnailPreview
          leftImageUrl={switchInThumbnail?.leftImageUrl}
          bottomImageUrl={switchInThumbnail?.bottomImageUrl}
        />
      </>
    );
  }

  return (
    <>
      {previewOverlay}
      <img alt={fileName} role='presentation' src={fileURL} className={styles.thumbnail} />
    </>
  );
}

export function FileItemTemplate({
  togglePreviewModal,
  fileName,
  fileURL,
  fileFormatSize,
  deleteItemCallback,
  canDeleteFile,
  canShowPreview = true,
  creativeCategory,
  switchInThumbnail,
}: FileItemTemplateProps) {
  const hasSwitchInThumbnail = Boolean(switchInThumbnail?.leftImageUrl || switchInThumbnail?.bottomImageUrl);

  return (
    <div className={styles.itemTemplateWrapper}>
      <div className={styles.fileInfo}>
        <div className={styles.thumbnailWrapper}>
          <FileItemThumbnail
            canShowPreview={canShowPreview}
            creativeCategory={creativeCategory}
            fileName={fileName}
            fileURL={fileURL}
            hasSwitchInThumbnail={hasSwitchInThumbnail}
            switchInThumbnail={switchInThumbnail}
            togglePreviewModal={togglePreviewModal}
          />
        </div>
        <span className={styles.fileName}>
          {fileName}

          <small className={styles.fileSize}>{fileFormatSize}</small>
        </span>
      </div>
      {canDeleteFile && deleteItemCallback && (
        <Button
          type='button'
          icon='pi pi-trash'
          className={styles.deleteButton}
          onClick={deleteItemCallback}
          data-testid={'delete-file-button'}
          aria-label='Delete file'
        />
      )}
    </div>
  );
}
